import React, { useState } from 'react';
import { PiiLevel, RedactionFramework } from '@shared/contracts';
import { ShieldCheck, EyeOff, CheckCircle2 } from 'lucide-react';

interface PiiRuleDef {
  level: PiiLevel;
  fields: string[];
  framework: RedactionFramework;
  action: 'RED_OUT' | 'BLACK_BOX' | 'PIXELATE' | 'HASH';
}

export const PiiRuleManagerPage: React.FC = () => {
  const [activeFramework, setActiveFramework] = useState<RedactionFramework>(RedactionFramework.GDPR);

  const rules: PiiRuleDef[] = [
    { level: PiiLevel.CRITICAL, fields: ['SSN', 'Passport Number', 'National ID', 'IBAN', 'Credit Card', 'Civil ID', 'SIN', 'CUIT'], framework: activeFramework, action: 'BLACK_BOX' },
    { level: PiiLevel.HIGH, fields: ['Full Name', 'Date of Birth', 'Driver License Number', 'Health ID', 'Biometric Photo'], framework: activeFramework, action: 'BLACK_BOX' },
    { level: PiiLevel.MEDIUM, fields: ['Physical Address', 'Phone Number', 'Personal Email', 'Employee Reference ID', 'VIN'], framework: activeFramework, action: 'PIXELATE' },
    { level: PiiLevel.LOW, fields: ['Issue Date', 'Expiry Date', 'Postal Code', 'Company Name', 'Vendor Code'], framework: activeFramework, action: 'HASH' },
  ];

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <EyeOff className="w-5 h-5 text-sky-600" />
            PII & Redaction Rules
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            PII classification sensitivity levels and compliance framework enforcement.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {Object.values(RedactionFramework).map((fw) => (
            <button
              key={fw}
              onClick={() => setActiveFramework(fw)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${
                activeFramework === fw
                  ? 'bg-sky-50 border-sky-200 text-sky-700'
                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {fw}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {rules.map((rule) => (
          <div
            key={rule.level}
            className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <ShieldCheck
                  className={`w-5 h-5 ${
                    rule.level === PiiLevel.CRITICAL
                      ? 'text-rose-600'
                      : rule.level === PiiLevel.HIGH
                      ? 'text-amber-600'
                      : 'text-emerald-600'
                  }`}
                />
                <h3 className="text-sm font-bold text-slate-900">{rule.level} SENSITIVITY</h3>
              </div>
              <span className="text-[11px] px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 font-mono border border-slate-200">
                {rule.action}
              </span>
            </div>

            <p className="text-xs text-slate-500">
              Automated redaction for compliance with {rule.framework} regulation.
            </p>

            <div>
              <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2 font-mono">
                Target Entity Fields
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {rule.fields.map((f) => (
                  <span key={f} className="text-xs px-2.5 py-1 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 font-medium">
                    {f}
                  </span>
                ))}
              </div>
            </div>

            <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
              <span>Auto-Purge Post Processing</span>
              <span className="text-emerald-700 font-semibold flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Enabled
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

